import React, { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery, Button, Stack } from "@mui/material";
import { useLocation, useSearchParams } from "react-router-dom";
import ProductGallery from "../../Components/Product/ProductGallery";
import SizeSelector from "../../Components/Product/SizeSelector";
import ColorSelector from "../../Components/Product/ColorSelector";
import SimilarProductsCard from "../../Components/Product/SimilarProductsCard";
import Footer from "../../Components/Common/Footer";
import { logUserAction } from "../../utils/analytics";

const ProductPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const searchQuery = location.state?.searchQuery || searchParams.get("q") || "";
  const product = location.state?.product || {};
  const trackingContext = location.state?.trackingContext || {};

  const allVariants = product?.variants || [];
  const uniqueColors = [...new Set(allVariants.map(v => v.color).filter(Boolean))];

  const [selectedColor, setSelectedColor] = useState(uniqueColors.length > 0 ? uniqueColors[0] : null);

  useEffect(() => {
    if (uniqueColors.length > 0) {
      setSelectedColor(uniqueColors[0]);
    } else {
      setSelectedColor(null);
    }
  }, [location.key]);

  const sizesToDisplay = React.useMemo(() => {
    let filtered = allVariants;
    if (selectedColor) {
      filtered = allVariants.filter(v => v.color === selectedColor);
    }
    const seen = new Set();
    return filtered.filter(v => {
      const rawSize = v.size;
      if (rawSize == null) return false;
      const sizeStr = String(rawSize).trim().toLowerCase();
      if (sizeStr === "" || sizeStr === "n/a" || sizeStr === "null" || sizeStr === "undefined") return false;

      if (seen.has(v.size)) return false;
      seen.add(v.size);
      return true;
    });
  }, [allVariants, selectedColor]);

  const mdScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [descExpanded, setDescExpanded] = useState(false);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "instant",
    });
  }, [location.key]);

  const handleCheckout = () => {
    if (product?.url) {
      window.open(product.url, "_blank");
    }
    logUserAction({
      action: "checkout_redirect",
      data: {
        product_id: product?.product_id || "",
        product_title: product?.title || "",
        brand: product?.brand || "",
        product_image: product?.image_url || null,
        redirect_url: product?.url || null,
        ...trackingContext,
      },
      timestamp: new Date().toISOString(),
    });
  };

  const ProductDetails = () => (
    <>
      <Typography
        sx={{
          fontSize: "13px",
          fontFamily: "Poppins-SemiBold",
          letterSpacing: "1px",
          color: "#999",
          textTransform: "uppercase",
          mb: 1,
        }}
      >
        {product?.brand}
      </Typography>

      <Typography
        sx={{
          color: "#1a1a1a",
          fontFamily: "'Lora', serif",
          fontWeight: 600,
          fontSize: { xs: "24px", md: "36px" },
          lineHeight: 1.2,
          letterSpacing: "-0.5px",
          mb: 2,
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        }}
      >
        {product?.title}
      </Typography>

      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Stack direction="row" alignItems="baseline" spacing={1.5}>
          <Typography
            sx={{
              fontSize: { xs: "24px", md: "28px" },
              fontWeight: 800,
              color: (product?.price?.discounted && product?.price?.original !== product?.price?.discounted) ? "#FF5252" : "#1a1a1a",
              fontFamily: "Poppins-Bold",
            }}
          >
            {product?.price?.discounted || product?.price?.original}
            <Typography
              component="span"
              sx={{
                fontSize: { xs: "16px", md: "18px" },
                fontWeight: 600,
                color: (product?.price?.discounted && product?.price?.original !== product?.price?.discounted) ? "#FF5252" : "#666",
                ml: 0.5,
              }}
            >
              {product?.price?.currency}
            </Typography>
          </Typography>

          {product?.price?.discounted && product?.price?.original !== product?.price?.discounted && (
            <Typography
              sx={{
                fontSize: { xs: "16px", md: "18px" },
                color: "#999",
                fontFamily: "Poppins-Regular",
                textDecoration: "line-through",
              }}
            >
              {product?.price?.original}
            </Typography>
          )}


        </Stack>
      </Box>

      {uniqueColors.length > 0 && (
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          <ColorSelector
            colors={uniqueColors}
            selectedColor={selectedColor}
            onSelectColor={setSelectedColor}
          />
        </Box>
      )}

      {sizesToDisplay.length > 0 && (
        <Box sx={{ mb: { xs: 4, md: 5 } }}>
          <SizeSelector sizes={sizesToDisplay} />
        </Box>
      )}

      {!mdScreen && (
        <Button
          fullWidth
          onClick={handleCheckout}
          sx={{
            background: "linear-gradient(110deg, #FFB79C 0%, #FFA785 40%, #FFD4C2 50%, #FFA785 60%, #FFB79C 100%)",
            backgroundSize: "200% auto",
            color: "#fff",
            fontFamily: "Poppins-SemiBold",
            fontSize: "16px",
            py: 2,
            borderRadius: "12px",
            textTransform: "none",
            boxShadow: "0 8px 24px rgba(255,183,156,0.4)",
            animation: "buyPulseRing 1.5s ease-out infinite, buyShimmer 5s ease-in-out infinite",
            animationDelay: "1s, 0s",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 12px 32px rgba(255,183,156,0.6)",
              animation: "none",
            }
          }}
        >
          Buy on {product?.brand}
        </Button>
      )}

      {product?.description && product.description !== "N/A" && (
        <Box sx={{ mt: { xs: 4, md: 6 }, pt: 3, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
          <Typography
            sx={{
              fontFamily: "Poppins-SemiBold",
              fontSize: "14px",
              color: "#1a1a1a",
              mb: 1.5,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Description
          </Typography>
          <Box
            sx={{
              maxHeight: "30vh",
              overflowY: "auto",
              pr: 2,
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { background: "rgba(0,0,0,0.02)" },
              "&::-webkit-scrollbar-thumb": { background: "rgba(0,0,0,0.1)", borderRadius: "4px" },
            }}
          >
            <Typography
              sx={{
                fontFamily: "Poppins-Regular",
                fontSize: "14px",
                color: "#666",
                lineHeight: 1.8,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
                ...(mdScreen && !descExpanded ? {
                  display: "-webkit-box",
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden"
                } : {})
              }}
            >
              {product?.description}
            </Typography>
            {mdScreen && (
              <Box
                onClick={() => setDescExpanded(!descExpanded)}
                sx={{
                  color: "#888",
                  fontFamily: "Poppins-SemiBold",
                  fontSize: "13px",
                  mt: 1.5,
                  cursor: "pointer",
                  display: "inline-block"
                }}
              >
                {descExpanded ? "Read less" : "Read more"}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </>
  );

  return (
    <>
      <style>
        {`
          @keyframes pageFadeUp {
            from { opacity: 0; transform: translateY(15px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes buyPulseRing {
            0% { box-shadow: 0 0 0 0 rgba(255, 167, 133, 0.7), 0 4px 14px rgba(255,183,156,0.5); transform: scale(1); }
            15% { transform: scale(1.04); }
            30% { box-shadow: 0 0 0 16px rgba(255, 167, 133, 0), 0 4px 14px rgba(255,183,156,0.5); transform: scale(1); }
            100% { box-shadow: 0 0 0 0 rgba(255, 167, 133, 0), 0 4px 14px rgba(255,183,156,0.5); transform: scale(1); }
          }
          @keyframes buyShimmer {
            0% { background-position: 200% center; }
            100% { background-position: -200% center; }
          }
        `}
      </style>

      <Box
        key={location.key}
        sx={{
          backgroundColor: "#FFFFFF",
          minHeight: "100vh",
          animation: "pageFadeUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards",
        }}
      >

        {/* Desktop Layout */}
        {!mdScreen && (
          <Box sx={{ width: "100%", display: "flex", maxWidth: "1600px", mx: "auto" }}>
            {/* Left Column: Huge Gallery (Dictates Page Scroll) */}
            <Box sx={{
              width: "50%",
              minWidth: 0,
              p: 4,
              pt: 0,
              pl: { xs: 4, lg: 8 }
            }}>
              <ProductGallery
                productImages={product?.images}
                fallbackImage={product?.image_url}
              />
            </Box>

            {/* Right Column: Details */}
            <Box sx={{ width: "55%", minWidth: 0 }}>
              <Box
                sx={{
                  px: 6,
                  py: 10,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <ProductDetails />
              </Box>
            </Box>
          </Box>
        )}

        {/* Mobile Layout */}
        {mdScreen && (
          <Box sx={{ pb: "100px" }}> {/* Padding for sticky bottom bar */}
            <ProductGallery
              productImages={product?.images}
              fallbackImage={product?.image_url}
            />

            <Box
              sx={{
                mt: "-32px",
                position: "relative",
                zIndex: 10,
                backgroundColor: "#fff",
                borderRadius: "32px 32px 0 0",
                pt: 4,
              }}
            >
              <Box sx={{ px: 3 }}>
                <ProductDetails />
              </Box>

              <Box sx={{ mt: 4 }}>
                <SimilarProductsCard product={product} hideBrand={false} />
              </Box>
            </Box>
          </Box>
        )}

        {/* Desktop Similar Products */}
        {!mdScreen && (
          <Box sx={{ maxWidth: "1600px", mx: "auto", px: 4, pt: 0, pb: 6, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
            <SimilarProductsCard product={product} hideBrand={false} />
          </Box>
        )}

        {!mdScreen && <Footer />}
      </Box>

      {/* Sticky Mobile Checkout Bar */}
      {mdScreen && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(12px)",
            borderTop: "1px solid rgba(0,0,0,0.05)",
            p: 2,
            px: 3,
            zIndex: 50,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 -4px 24px rgba(0,0,0,0.06)"
          }}
        >
          <Box>
            <Typography sx={{ fontFamily: "Poppins-SemiBold", fontSize: "13px", color: "#666", textTransform: "uppercase" }}>Total</Typography>
            <Typography sx={{ fontFamily: "Poppins-Bold", fontSize: "22px", color: "#1a1a1a", lineHeight: 1 }}>
              {product?.price?.discounted || product?.price?.original}
            </Typography>
          </Box>
          <Button
            onClick={handleCheckout}
            sx={{
              background: "linear-gradient(110deg, #FFB79C 0%, #FFA785 40%, #FFD4C2 50%, #FFA785 60%, #FFB79C 100%)",
              backgroundSize: "200% auto",
              color: "#fff",
              fontFamily: "Poppins-SemiBold",
              fontSize: "14px",
              px: 5,
              py: 1.5,
              minWidth: "140px",
              borderRadius: "24px",
              textTransform: "none",
              boxShadow: "0 4px 14px rgba(255,183,156,0.5)",
              animation: "buyPulseRing 1.5s ease-out infinite, buyShimmer 5s ease-in-out infinite",
              animationDelay: "1s, 0s",
            }}
          >
            Buy on {product?.brand}
          </Button>
        </Box>
      )}
    </>
  );
};

export default ProductPage;
